import { WebSocketService } from '../../services/WebSocketService';
import {
  CourtSession,
  RoomMember,
  PaymentRecord,
  RoomEventCallbacks,
} from '../types';

/**
 * CourtSplitterSync — Real-time sync via WebSocket.
 * Subscribes to room channels and dispatches events to callbacks.
 * Falls back to polling when WebSocket is unavailable.
 */
export class CourtSplitterSync {
  private wsService: WebSocketService;
  private subscriptions: Map<string, RoomEventCallbacks> = new Map();
  private pollIntervals: Map<string, ReturnType<typeof setInterval>> = new Map();
  private userId: string = '';

  constructor() {
    this.wsService = WebSocketService.getInstance();
  }

  /** Set current user ID for filtering own events */
  setUserId(userId: string): void {
    this.userId = userId;
  }

  /** Subscribe to real-time updates for a room */
  subscribe(roomCode: string, callbacks: RoomEventCallbacks): void {
    this.subscriptions.set(roomCode, callbacks);

    // Try WebSocket subscription
    if (this.checkConnected()) {
      try {
        this.wsService.send({
          action: 'court-splitter:subscribe',
          roomCode,
          userId: this.userId,
        });
      } catch {
        // WebSocket not ready, fall back to polling
        this.startPolling(roomCode);
      }

      // Set up message handler
      this.wsService.setCustomMessageHandler((message: any) => {
        this.handleMessage(message);
      });
    } else {
      // Fallback to polling
      this.startPolling(roomCode);
    }
  }

  /** Unsubscribe from room updates */
  unsubscribe(roomCode: string): void {
    this.subscriptions.delete(roomCode);

    // Unsubscribe via WebSocket
    if (this.checkConnected()) {
      try {
        this.wsService.send({
          action: 'court-splitter:unsubscribe',
          roomCode,
        });
      } catch {
        // Ignore send errors during unsubscribe
      }
    }

    // Stop polling if active
    const interval = this.pollIntervals.get(roomCode);
    if (interval) {
      clearInterval(interval);
      this.pollIntervals.delete(roomCode);
    }
  }

  /** Unsubscribe from all rooms */
  unsubscribeAll(): void {
    for (const roomCode of this.subscriptions.keys()) {
      this.unsubscribe(roomCode);
    }
  }

  /** Handle incoming WebSocket message */
  private handleMessage(message: any): void {
    if (!message || !message.type || !message.roomCode) return;

    // Ignore own events
    if (message.senderId === this.userId) return;

    const callbacks = this.subscriptions.get(message.roomCode);
    if (!callbacks) return;

    switch (message.type) {
      case 'court-splitter:sessionAdded':
        callbacks.onSessionAdded?.(message.session as CourtSession);
        break;
      case 'court-splitter:sessionEdited':
        callbacks.onSessionEdited?.(message.session as CourtSession);
        break;
      case 'court-splitter:sessionDeleted':
        callbacks.onSessionDeleted?.(message.sessionId as string);
        break;
      case 'court-splitter:memberJoined':
        callbacks.onMemberJoined?.(message.member as RoomMember);
        break;
      case 'court-splitter:memberRemoved':
        callbacks.onMemberRemoved?.(message.memberId as string);
        break;
      case 'court-splitter:paymentRecorded':
        callbacks.onPaymentRecorded?.(message.payment as PaymentRecord);
        break;
    }
  }

  /** Start polling fallback (30s interval) */
  private startPolling(roomCode: string): void {
    // Clear existing interval if any
    const existing = this.pollIntervals.get(roomCode);
    if (existing) clearInterval(existing);

    const interval = setInterval(() => {
      // If WebSocket reconnects, stop polling and switch
      if (this.checkConnected()) {
        clearInterval(interval);
        this.pollIntervals.delete(roomCode);
        try {
          this.wsService.send({
            action: 'court-splitter:subscribe',
            roomCode,
            userId: this.userId,
          });
        } catch {
          // Will retry on next interval
        }
        return;
      }

      // Polling would trigger a background refresh in the service layer
      // The component re-renders when cache updates
    }, 30000);

    this.pollIntervals.set(roomCode, interval);
  }

  /** Check if WebSocket is connected */
  private checkConnected(): boolean {
    try {
      // WebSocketService doesn't expose isConnected directly,
      // but we can check if getRoomCode returns something (indicates active connection)
      // For safety, we just try to send and catch errors
      return !!(this.wsService as any).ws && (this.wsService as any).ws.readyState === WebSocket.OPEN;
    } catch {
      return false;
    }
  }

  /** Check if WebSocket is connected (public) */
  isConnected(): boolean {
    return this.checkConnected();
  }
}
